import { EnvironmentProviders, Provider } from "@angular/core";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from "@azure/msal-browser";
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from "@azure/msal-angular";
import {
  AppConfigService,
  InterceptorConfig,
  KnownConfigKey,
  MsalConfig,
} from "./services/app.config.service";

import { ROUTES } from "../app/common/constants";

type MsalClientConfig = {
  authority: string;
  tenant: string;
  policy: string;
};

const toMsalAuthority: (config: MsalClientConfig) => string = ({
  authority,
  tenant,
  policy,
}) => {
  return `${authority}/tfp/${tenant}/${policy}`;
};

function loggerCallback(logLevel: LogLevel, message: string) {
  // console.log(message);
}

const withLeadingSlash: (val: string) => string = (val) => `/${val}`;

function MSALInstanceFactory(
  configService: AppConfigService
): IPublicClientApplication {
  const {
    auth,
    auth: { clientId, authority },
  } = configService.getSetting<MsalConfig>(KnownConfigKey.msalConfig);

  return new PublicClientApplication({
    auth: {
      clientId: clientId,
      authority: toMsalAuthority(auth),
      knownAuthorities: [authority],
      redirectUri: withLeadingSlash(ROUTES.authenticationRedirect),
      postLogoutRedirectUri: withLeadingSlash(ROUTES.home),
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

function MSALInterceptorConfigFactory(
  appConfigService: AppConfigService
): MsalInterceptorConfiguration {
  const { interactionType, protectedResourceMap: resourceMap } =
    appConfigService.getSetting<InterceptorConfig>(KnownConfigKey.interceptor);

  const protectedResourceMap = new Map<string, Array<string>>(resourceMap);

  return {
    interactionType: interactionType as
      | InteractionType.Redirect
      | InteractionType.Popup,
    protectedResourceMap,
  };
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [],
    },
    loginFailedRoute: "/login-failed",
  };
}

const providers: Array<Provider | EnvironmentProviders> = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: MsalInterceptor,
    multi: true,
  },
  {
    provide: MSAL_INSTANCE,
    deps: [AppConfigService],
    useFactory: MSALInstanceFactory,
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory,
  },
  {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: MSALInterceptorConfigFactory,
    deps: [AppConfigService],
  },
  MsalService,
  MsalGuard,
  MsalBroadcastService,
];

export const getMsalProviders: () => Array<
  Provider | EnvironmentProviders
> = () => Array.from(providers);
